import { useMutation, useQuery } from '@apollo/client'
import {
    Box,
    Paper,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import React, { FC, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CandidatePreferencesQuery } from 'recruiter/graphql/candidatePreferences.gql'
import useToast from 'shared/components/Toast/useToast'
import {
    UpdateCandidateIndustryPreferences,
    UpdateCandidateLocationPreferences,
    UpdateCandidateSectorPreferences,
    UpdateCandidateStartDate,
} from 'shared/graphql/candidatePreferences.gql'
import { getPxAsRem } from 'shared/utils'
import Industry from './Industry'
import Location from './Location'
import Sector from './Sector'
import StartDate from './StartDate'

const RecruitingPreferences: FC = () => {
    const [selectedTab, setSelectedTab] = useState('industry')
    const { candidateId } = useParams()
    const toast = useToast()

    const { data, refetch } = useQuery(CandidatePreferencesQuery, {
        variables: { candidateId },
    })

    const [updateLocationPreferences] = useMutation(
        UpdateCandidateLocationPreferences,
        {
            onCompleted: () => refetch(),
            onError: (error) => {
                refetch()
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: error.message,
                })
            },
        }
    )
    const [updateIndustryPreferences] = useMutation(
        UpdateCandidateIndustryPreferences,
        {
            onCompleted: () => refetch(),
            onError: (error) => {
                refetch()
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: error.message,
                })
            },
        }
    )
    const [updateSectorPreferences] = useMutation(
        UpdateCandidateSectorPreferences,
        {
            onCompleted: () => refetch(),
            onError: (error) => {
                refetch()
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: error.message,
                })
            },
        }
    )
    const [updateStartDate] = useMutation(UpdateCandidateStartDate, {
        onCompleted: () => refetch(),
        onError: (error) => {
            refetch()
            toast({
                severity: 'error',
                title: 'Error',
                message: error.message,
            })
        },
    })

    const handleChange = (
        event: React.MouseEvent<HTMLElement>,
        newTab: string
    ) => {
        setSelectedTab(newTab)
    }

    const sortByTime = (a, b) => {
        return (
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime()
        )
    }

    const preferredStartDate = useMemo(() => {
        return (
            data?.candidate?.startDate
                ?.map((startDate) => startDate.value)
                .filter((startDate) => typeof startDate === 'string') || []
        )
    }, [data?.candidate?.startDate])

    const {
        updatedAtIndustry,
        updatedByIndustry,
        updatedAtSector,
        updatedBySector,
        updatedAtLocation,
        updatedByLocation,
        updatedAtStartDate,
        updatedByStartDate,
    } = useMemo(() => {
        if (!data) return {}
        const industryPreferences = data?.candidate?.industryPreferences || []
        const mostRecentlyUpdatedIndustry = [...industryPreferences].sort(
            sortByTime
        )?.[0]

        const updatedAtIndustry =
            mostRecentlyUpdatedIndustry?.updatedAt ||
            mostRecentlyUpdatedIndustry?.createdAt

        const updatedByIndustry =
            mostRecentlyUpdatedIndustry?.updatedByUser ||
            mostRecentlyUpdatedIndustry?.createdByUser

        const sectorPreferences = data?.candidate?.sectorPreferences || []
        const mostRecentlyUpdatedSector = [...sectorPreferences].sort(
            sortByTime
        )?.[0]

        const updatedAtSector =
            mostRecentlyUpdatedSector?.updatedAt ||
            mostRecentlyUpdatedSector?.createdAt
        const updatedBySector =
            mostRecentlyUpdatedSector?.updatedByUser ||
            mostRecentlyUpdatedSector?.createdByUser

        const locationPreferences = data?.candidate?.locationPreferences || []
        const mostRecentlyUpdatedLocation = [...locationPreferences].sort(
            sortByTime
        )?.[0]

        const updatedAtLocation =
            mostRecentlyUpdatedLocation?.updatedAt ||
            mostRecentlyUpdatedLocation?.createdAt
        const updatedByLocation =
            mostRecentlyUpdatedLocation?.updatedByUser ||
            mostRecentlyUpdatedLocation?.createdByUser

        const startDate = data.candidate?.startDate || []
        const mostRecentlyUpdatedStartDate = [...startDate].sort(
            sortByTime
        )?.[0]
        const updatedAtStartDate =
            mostRecentlyUpdatedStartDate?.updatedAt ||
            mostRecentlyUpdatedStartDate?.createdAt
        const updatedByStartDate =
            mostRecentlyUpdatedStartDate?.updatedByUser ||
            mostRecentlyUpdatedStartDate?.createdByUser

        return {
            updatedAtIndustry,
            updatedByIndustry,
            updatedAtSector,
            updatedBySector,
            updatedAtLocation,
            updatedByLocation,
            updatedAtStartDate,
            updatedByStartDate,
        }
    }, [data])

    const selectedTabContent = useMemo(() => {
        if (!data)
            return (
                <Stack gap={2}>
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="rounded" height={40} />
                    <Skeleton variant="rounded" height={40} />
                </Stack>
            )
        switch (selectedTab) {
            case 'industry':
                return (
                    <Industry
                        industryPreferences={
                            data?.candidate?.industryPreferences || []
                        }
                        candidateId={candidateId}
                        handleUpdate={updateIndustryPreferences}
                        industryOptions={data?.preferenceIndustries || []}
                        updatedAt={updatedAtIndustry}
                        updatedBy={`${updatedByIndustry?.firstName || ''} ${updatedByIndustry?.lastName || ''}`}
                    />
                )
            case 'sector':
                return (
                    <Sector
                        sectorPreferences={
                            data?.candidate?.sectorPreferences || []
                        }
                        handleUpdate={updateSectorPreferences}
                        candidateId={candidateId}
                        sectorOptions={data?.preferenceSectors || []}
                        updatedAt={updatedAtSector}
                        updatedBy={`${updatedBySector?.firstName || ''} ${updatedBySector?.lastName || ''}`}
                    />
                )
            case 'location':
                return (
                    <Location
                        locationPreferences={
                            data?.candidate?.locationPreferences || []
                        }
                        handleUpdate={updateLocationPreferences}
                        candidateId={candidateId}
                        locationOptions={data?.preferenceLocations || []}
                        updatedAt={updatedAtLocation}
                        updatedBy={`${updatedByLocation?.firstName || ''} ${updatedByLocation?.lastName || ''}`}
                    />
                )
            case 'startDate':
            default:
                return (
                    <StartDate
                        preferredStartDate={preferredStartDate}
                        handleUpdate={updateStartDate}
                        candidateId={candidateId}
                        updatedAt={updatedAtStartDate}
                        updatedBy={`${updatedByStartDate?.firstName || ''} ${updatedByStartDate?.lastName || ''}`}
                    />
                )
        }
    }, [
        data,
        preferredStartDate,
        selectedTab,
        candidateId,
        updateIndustryPreferences,
        updateLocationPreferences,
        updateSectorPreferences,
        updateStartDate,
        updatedAtIndustry,
        updatedAtLocation,
        updatedAtSector,
        updatedAtStartDate,
        updatedByIndustry?.firstName,
        updatedByIndustry?.lastName,
        updatedByLocation?.firstName,
        updatedByLocation?.lastName,
        updatedBySector?.firstName,
        updatedBySector?.lastName,
        updatedByStartDate?.firstName,
        updatedByStartDate?.lastName,
    ])

    return (
        <Paper
            sx={{
                display: 'flex',
                gap: getPxAsRem(10),
                flexDirection: 'column',
                padding: getPxAsRem(10),
                height: { xs: 'auto', md: getPxAsRem(550) },
                overflowY: { md: 'auto' },
            }}
        >
            <Box>
                <Typography variant="h4">Recruiting Preferences</Typography>
            </Box>
            <ToggleButtonGroup
                aria-label="preferences category"
                value={selectedTab}
                exclusive
                onChange={handleChange}
                size="small"
                color="primary"
                sx={{
                    marginBottom: getPxAsRem(10),
                    ' .MuiButtonBase-root': {
                        padding: getPxAsRem(4),
                    },
                }}
                fullWidth
            >
                <ToggleButton value="industry" size="small">
                    Industry
                </ToggleButton>
                <ToggleButton value="sector" size="small">
                    Sector
                </ToggleButton>
                <ToggleButton value="location" size="small">
                    Location
                </ToggleButton>
                <ToggleButton value="startDate" size="small">
                    Start Date
                </ToggleButton>
            </ToggleButtonGroup>
            {selectedTabContent}
        </Paper>
    )
}

export default RecruitingPreferences
